// src/types/redux.ts (Updated for T-23 Local Persistence and T-24 UI Enhancements)
/**
 * Redux state type definitions
 * UPDATED: Added DraftState for T-23 local persistence
 * UPDATED: Added T-24 UI enhancements and new interfaces
 */

export interface Fraction {
  n: number; // numerator
  d: number; // denominator (invariant: d > 0, gcd(n,d)=1 if simplified)
}

/**
 * Phase 3: Number Format Configuration
 * Unified format configuration for both primary and secondary lines
 */
export interface NumberFormatConfig {
  /** Base number type */
  type: 'integer' | 'decimal' | 'fraction' | 'percentage' | 'currency' | 'custom';
  
  /** Decimal places (0-3 or 'auto') - only used when type is 'decimal' or 'currency' */
  decimalPlaces?: number | 'auto';
  
  /** Fraction format - only used when type is 'fraction' */
  fractionFormat?: 'mixed' | 'improper';
  
  /** Whether to simplify fractions - only used when type is 'fraction' */
  fractionSimplify?: boolean;
  
  /** Currency symbol (£, $, €, etc.) - only used when type is 'currency' */
  currencySymbol?: string;
  
  /** Currency position relative to number */
  currencyPosition?: 'before' | 'after';
  
  /** Custom unit suffix (g, ml, km, etc.) - only used when type is 'custom' */
  customUnit?: string;
  
  /** Unit position relative to number */
  unitPosition?: 'before' | 'after';
  
  /** Show unit on all labels or only on select labels */
  showUnitOnAllLabels?: boolean;
}

/**
 * Default number format configuration
 */
export const defaultNumberFormatConfig: NumberFormatConfig = {
  type: 'integer',
  decimalPlaces: 'auto',
  fractionFormat: 'improper',
  fractionSimplify: true,
  currencySymbol: '£',
  currencyPosition: 'before',
  customUnit: '',
  unitPosition: 'after',
  showUnitOnAllLabels: false,
};

export interface Domain {
  start: number;
  end: number;
}

// Viewport State (Section 5.1) - Updated for T-24
export interface ViewportState {
  scale: number; // pixels per unit
  offsetX: number; // horizontal offset
  offsetY: number; // T-24: Added vertical offset
  orientation: 'horizontal' | 'vertical';
  showNegatives: boolean;
  isDragging: boolean; // T-24: Added dragging state
  lastDragPosition: { x: number; y: number } | null; // T-24: Added last drag position
}

// Number Line Configuration (Section 5.2) - Updated for T-24 and Phase 3
export interface NumberLineState {
  id: string;
  unitInterval: Fraction;
  tickMode: 'none' | 'ticks' | 'labeled';
  labelPosition: 'below' | 'above';
  
  // DEPRECATED: Kept for backward compatibility
  numberType?: 'integer' | 'decimal' | 'fraction' | 'percent';
  
  // NEW Phase 3: Unified number format configuration
  numberFormat?: NumberFormatConfig;
  
  // DEPRECATED: Moved into numberFormat
  fractionDisplay?: {
    format: 'mixed' | 'improper';
    simplify: boolean;
  };
  
  subTickDivisions: number | null;
}

// T-24: New NumberLinesState interface for enhanced number line management
export interface NumberLinesState {
  primary: {
    enabled: boolean;
    start: number;
    end: number;
    step: number;
  };
  secondary: {
    enabled: boolean;
    start: number;
    end: number;
    step: number;
    scaleFactor: Fraction;
  };
  numberType: 'integer' | 'fraction' | 'decimal';
  showTicks: boolean;
  showLabels: boolean;
  showMidpoints: boolean;
}

// Equivalent Fractions Mode (Stacked lines)
export type ViewMode = 'standard' | 'equivalentFractions' | 'coordinateGrid';

export interface EquivalentFractionsState {
  mode: ViewMode;
  denominators: number[];
  /**
   * Session-only active denominators (sorted, unique).
   * Initialized from persisted denominators but not saved to preferences.
   */
  activeDenominators: number[];
  integerLabelsOnly: boolean;
  labelFormat: 'fraction' | 'decimal';
  /**
   * Saved viewport states for each mode to allow independent zooming.
   */
  savedViewports?: {
    [key in ViewMode]?: ViewportState;
  };
}

// Secondary Line State (Section 5.3) - Updated for T-24 and Phase 3
export interface SecondaryLineState {
  enabled: boolean;
  scaleFactor: Fraction;
  /** When true, secondary labels reuse the primary line's format settings */
  inheritFormatFromPrimary?: boolean;
  
  // DEPRECATED: Kept for backward compatibility migration
  numberType?: 'integer' | 'decimal' | 'fraction' | 'percent';
  
  // NEW Phase 3: Unified number format configuration
  numberFormat: NumberFormatConfig;
  
  // DEPRECATED: Moved into numberFormat.fractionFormat and fractionSimplify
  fractionDisplay?: {
    format: 'mixed' | 'improper';
    simplify: boolean;
  };
  
  locked: boolean; // if always proportional
  offset: number; // T-24: Added offset
  visible: boolean; // T-24: Added visibility
  hideAllLabels: boolean; // Hide all secondary line labels
}

// Label Visibility Overrides (Section 5.4)
export interface LabelVisibilityState {
  [valueKey: string]: {
    hidden: boolean;
    /** Optional equivalent fractions line context (denominator). */
    equivalentDenominator?: number;
  };
}

// T-10: Custom Ticks State (NEW)
export interface CustomTick {
  id: string;
  value: number;
  label?: string; // Optional custom label, defaults to calculated from value
  type: 'midpoint' | 'custom';
  createdAt: number; // timestamp
  /** Optional equivalent fractions line context (denominator). */
  equivalentDenominator?: number;
}

export interface CustomTicksState {
  ticks: CustomTick[];
  nextId: number;
}

// Multiplier markers (primary→secondary scaling helpers)
export interface MultiplierMarker {
  id: string;
  value: number;
}

export interface MultiplierMarkerState {
  markers: MultiplierMarker[];
  nextId: number;
}

// Jump Indicators (Section 5.5)
// Import from jump.ts to ensure consistency with Phase 2 enhancements
export type { Jump, JumpIndicatorsState } from './jump';

// Annotation Objects (Section 5.6)
export interface AnnotationShape {
  id: string;
  type: 'rectangle' | 'rectangle-filled' | 'circle' | 'line';
  geometry: {
    x: number;
    y: number;
    width?: number;
    height?: number;
    x2?: number;
    y2?: number;
    radius?: number;
  };
  style: {
    color: string;
    thickness: number;
    arrowStart?: boolean;
    arrowEnd?: boolean;
  };
  zIndex: number;
  selected: boolean;
}

export interface AnnotationText {
  id: string;
  type: 'text' | 'equation';
  content: string;
  position: { x: number; y: number };
  style: {
    fontSize: number;
    color: string;
    fontFamily?: string;
  };
  zIndex: number;
  selected: boolean;
}

export interface AnnotationStroke {
  id: string;
  type: 'pen' | 'highlighter';
  points: Array<{ x: number; y: number; pressure?: number }>;
  style: {
    color: string;
    thickness: number;
    opacity: number;
  };
  zIndex: number;
  selected: boolean;
}

export interface AnnotationsState {
  shapes: AnnotationShape[];
  texts: AnnotationText[];
  strokes: AnnotationStroke[];
  nextId: number;
}

// Expression Objects (Section 22)
export interface ExpressionObject {
  id: string;
  source: string; // raw user text
  value?: Fraction; // last successful evaluation
  error?: string; // parse or eval error message
  position?: { x: number; y: number }; // if positioned on canvas
}

// Note: The actual ExpressionsState interface is defined in src/store/slices/expressionsSlice.ts
// This is kept for backward compatibility but should not be used
// REMOVED: Duplicate ExpressionsState interface - use the one from expressionsSlice.ts

// Constants Overlay (Section 24.3) - Updated for T-24
export interface ConstantsState {
  enabled: boolean;
  precision: number; // 2-6 decimal places
  visibleConstants: string[]; // subset of available constants
  showLabels: boolean; // Whether to show the text labels
  labelPosition: 'above' | 'below' | 'left' | 'right'; // Label positioning relative to marker
  // T-24: Enhanced constants structure
  constants: Array<{
    id: string;
    name: string;
    value: number;
    symbol: string;
    precision: number;
    visible: boolean;
  }>;
}

// UI State (Section 5.7) - Updated for T-24
export type ToolType = 
  | 'select'
  | 'pan'
  | 'jump'
  | 'text'
  | 'equation' 
  | 'pen'
  | 'highlighter'
  | 'eraser'
  | 'rectangle'
  | 'rectangle-filled'
  | 'circle'
  | 'line'
  | 'measurement'
  | 'zoom' // T-24: Added zoom tool
  | 'point' // T-24: Added point tool
  | 'measure' // T-24: Added measure tool
  | 'annotate'; // T-24: Added annotate tool

// Tool-specific settings interface
export interface ToolSettings {
  color: string;
  thickness: number;
  arrowStart?: boolean;
  arrowEnd?: boolean;
}

// Measurement object type for T-16
export interface MeasurementObject {
  id: string;
  startValue: number;
  endValue: number;
  selected: boolean;
  isSnapped?: boolean;
  snappedStartValue?: number;
  snappedEndValue?: number;
  // Phase 6: 2D Coordinate Grid support
  /** For 2D: point1 coordinates */
  point1?: { x: number; y: number };
  /** For 2D: point2 coordinates */
  point2?: { x: number; y: number };
  /** For 2D: show Pythagorean calculation */
  showCalculation?: boolean;
  /** For 2D: show horizontal/vertical components */
  showComponents?: boolean;
  /** For 2D: display mode */
  displayMode?: 'exact' | 'decimal' | 'both';
}

// =============================================================================
// T-22: SHARE FLOW TYPES
// =============================================================================

export interface ShareState {
  isLoading: boolean;
  lastShareCode?: string;
  shareUrl?: string;
  loadedFromShare: boolean;
}

// Update UIState to include share dialogs and T-24 enhancements
export interface UIState {
  activeTool: ToolType;
  selectedObjects: string[];
  dialogs: {
    shareDialog: boolean;        // T-22: Share dialog state
    loadDialog: boolean;         // T-22: Load dialog state
    scaleFactorDialog: boolean;
    jumpToValueDialog: boolean;
    exportDialog: boolean;
    preferencesDialog: boolean;  // T-24: Added preferences dialog
  };
  modals: {
    exportDialog: boolean;
    helpDialog: boolean;
  };
  panels: {
    constants: boolean;
    expressions: boolean;
    annotations: boolean;
  };
  colorPicker: {
    open: boolean;
    activeColor: string;
  };
  toolSettings: Record<ToolType, ToolSettings>;
  thickness: number;
  loading: boolean;
  errorMessage?: string;
  dragSnap: {
    enabled: boolean;
    threshold: number;
    showIndicators: boolean;
    snapToTicks: boolean;
    snapToCustomTicks: boolean;
  };
  measurement: {
    isActive: boolean;
    isDragging: boolean;
    measurements: MeasurementObject[];
    nextId: number;
    startPoint?: { x: number; value: number; isSnapped?: boolean; snappedValue?: number };
    endPoint?: { x: number; value: number; isSnapped?: boolean; snappedValue?: number };
  };
  annotationLayer: {
    panWithNumberLine: boolean;
  };
  shareUrlState: {
    detectedSceneId: string | null;
    isAutoLoading: boolean;
    autoLoadError: string | null;
    showAutoLoadPrompt: boolean;
  };
  notifications: Array<{
    id: string;
    type: 'info' | 'success' | 'warning' | 'error';
    title: string;
    message?: string;
    duration?: number;
  }>;
  keyboardShortcutsEnabled: boolean;
  accessibilityMode: boolean;
  // Global view toggles
  mainLabelsHidden?: boolean; // When true, hide all primary number line labels
  // Session-only tick subdivision override (does not change preferences default)
  tickSubdivisionMode?: SubdivisionMode;
  /**
   * Equivalent fractions helper: optional integer-only top line toggle
   * Defaults to true when undefined for backward compatibility.
   */
  showEquivalentIntegerLine?: boolean;
  /**
   * Add Point dialog state for DNL custom points
   */
  addPointDialog?: {
    isOpen: boolean;
    targetLine: 'primary' | 'secondary';
    initialValue?: number;
  };
}

// T-22: Share workflow result types
export interface ShareResult {
  id: string;
  url: string;
  metadata: {
    id: string;
    size_bytes: number;
    schema_version: string;
    created_at: string;
  };
}

export interface LoadResult {
  state: any;
  metadata: {
    id: string;
    size_bytes: number;
    schema_version: string;
    created_at: string;
  };
}

// T-22: URL handling state
export interface ShareUrlState {
  detectedSceneId: string | null;
  isAutoLoading: boolean;
  autoLoadError: string | null;
  hasPrompted: boolean;
}

// Preferences (Section 5.9) - Updated for T-24
export type LocaleCode = 'en-GB' | 'en-US' | 'de-DE' | 'fr-FR' | 'es-ES'; // T-24: Added en-US and es-ES
export type ThemeMode = 'light' | 'dark' | 'auto';

/**
 * Subdivision mode for hierarchical tick generation
 * - smart: Auto-detect best pattern based on unit interval
 * - decimal: Powers of 10 (10ths, 100ths, 1000ths)
 * - binary: Halves, quarters, eighths, sixteenths
 * - halves-tenths: Halves, quarters, then tenths
 */
export type SubdivisionMode = 'smart' | 'decimal' | 'binary' | 'halves-tenths';

export interface PreferencesState {
  theme: ThemeMode;
  locale: LocaleCode;
  exportBackground: 'transparent' | 'white' | 'black';
  autoSave: boolean;
  keyboardShortcuts: boolean;
  accessibilityMode: boolean;
  // Persist these across sessions
  constantsPrecision: number;
  defaultScaleFactor: Fraction;
  preferredOrientation: 'horizontal' | 'vertical';
  showInstallPrompt: boolean; // Whether to show the install prompt
  defaultJumpSize: number; // Default jump size for pan toolbar buttons
  // Scroll/interaction preferences
  wheelReverseScroll: boolean; // Reverse pan direction for wheel/trackpad
  wheelScrollSensitivity: number; // Multiplier for wheel pan sensitivity (e.g., 0.25 - 4)
  // Layout preferences
  sidebarDefaultOpen?: boolean; // Whether the left toolbar starts open on desktop (default true)
  // Global text scaling (1.0 = default)
  textScale?: number;
  // Hierarchical tick subdivision mode
  subdivisionMode?: SubdivisionMode;
}

// T-24: New PersistenceState interface
export interface PersistenceState {
  lastSaveTime: string | null;
  autoSaveEnabled: boolean;
  hasDraft: boolean;
  loadedFromDraft: boolean;
  saveInProgress: boolean;
  saveError: string | null;
}

// =============================================================================
// T-23: DRAFT PERSISTENCE TYPES  
// =============================================================================

// Import DraftState from the draft slice
import type { DraftState } from '../store/slices/draftSlice';
// Import ExpressionsState from the expressions slice
import type { ExpressionsState } from '../store/slices/expressionsSlice';
// Import ToolModeState from the tool mode slice
import type { ToolModeState } from '../store/slices/toolModeSlice';
// Import CoordinateGridState from coordinate grid types
import type { CoordinateGridState } from './coordinateGrid';
// Import DNLToolsState from dnl types
import type { DNLToolsState } from './dnl';
// Import Phase 3 state types
import type { PlotPointsState } from '../store/slices/plotPointsSlice';
import type { GridShapesState } from '../store/slices/gridShapesSlice';
// Import Phase 7 state types
import type { VectorsState } from '../store/slices/vectorsSlice';
import type { InequalitiesState } from '../store/slices/inequalitiesSlice';

// DraftState is now imported from draftSlice.ts - this ensures single source of truth

// =============================================================================
// UPDATED ROOT STATE
// =============================================================================

/**
 * Enhanced RootState interface with T-23 draft support and T-24 enhancements
 * This extends the existing RootState to include the draft slice and new features
 */
export interface EnhancedRootState {
  viewport: ViewportState;
  numberLines: NumberLineState;
  secondaryLine: SecondaryLineState;
  equivalentFractions: EquivalentFractionsState;
  labelVisibility: LabelVisibilityState;
  customTicks: CustomTicksState;
  multipliers: MultiplierMarkerState;
  jumpIndicators: JumpIndicatorsState;
  annotations: AnnotationsState;
  expressions: ExpressionsState;
  constants: ConstantsState;
  ui: UIState;
  share: ShareState;
  preferences: PreferencesState;
  draft: DraftState;  // NEW: T-23 draft persistence
  toolMode: ToolModeState; // NEW: T-34 tool mode management
  persistence?: PersistenceState; // T-24: Optional persistence state
  coordinateGrid: CoordinateGridState;
  dnlTools: DNLToolsState; // NEW: DNL Tools state
  plotPoints: PlotPointsState; // NEW: Phase 3 - Plotted points
  gridShapes: GridShapesState; // NEW: Phase 3 - Grid shapes
  vectors: VectorsState; // NEW: Phase 7 - Vectors
  inequalities: InequalitiesState; // NEW: Phase 7 - Inequalities
}

// =============================================================================
// TYPE COMPATIBILITY
// =============================================================================

/**
 * For backward compatibility, we'll use the enhanced version as RootState
 * This ensures existing code continues to work
 */

// =============================================================================
// PERSISTENCE-SPECIFIC TYPES
// =============================================================================

/**
 * State slices that should be included in persistence
 */
export type PersistableSliceNames = 
  | 'viewport'
  | 'numberLines'
  | 'secondaryLine'
  | 'equivalentFractions'
  | 'labelVisibility'
  | 'customTicks'
  | 'jumpIndicators'
  | 'expressions'
  | 'constants'
  | 'preferences';

/**
 * State slices that should NOT be persisted (transient state)
 */
export type TransientSliceNames = 
  | 'ui'
  | 'share'
  | 'draft'; // draft slice manages its own persistence

/**
 * Partial state for draft restoration
 * Only includes persistable slices
 */
export type DraftRestorableState = Partial<Pick<EnhancedRootState, PersistableSliceNames>>;

/**
 * Metadata about a draft for UI display
 */
export interface DraftDisplayInfo {
  available: boolean;
  size: string;
  timestamp: string;
  sliceCount: number;
  hasAnnotations: boolean;
}

/**
 * Options for draft operations
 */
export interface DraftOperationOptions {
  forceOverwrite?: boolean;
  showConfirmation?: boolean;
  clearExisting?: boolean;
}

// =============================================================================
// AUTO-SAVE CONFIGURATION TYPES
// =============================================================================

/**
 * Configuration for auto-save middleware
 */
export interface AutoSaveConfig {
  enabled: boolean;
  debounceMs: number;
  maxSize: number;
  excludeAnnotations: boolean;
  logOperations: boolean;
}

/**
 * Auto-save operation result
 */
export interface AutoSaveResult {
  success: boolean;
  timestamp: string;
  size: number;
  excludedData?: string[];
  error?: string;
}

// =============================================================================
// ENHANCED TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if a slice name is persistable
 */
export function isPersistableSlice(sliceName: string): sliceName is PersistableSliceNames {
  const persistableSlices: PersistableSliceNames[] = [
    'viewport',
    'numberLines', 
    'secondaryLine',
    'equivalentFractions',
    'labelVisibility',
    'customTicks',
    'jumpIndicators',
    'expressions',
    'constants',
    'preferences',
  ];
  
  return persistableSlices.includes(sliceName as PersistableSliceNames);
}

/**
 * Type guard to check if a slice name is transient
 */
export function isTransientSlice(sliceName: string): sliceName is TransientSliceNames {
  const transientSlices: TransientSliceNames[] = ['ui', 'share', 'draft'];
  return transientSlices.includes(sliceName as TransientSliceNames);
}

/**
 * Extract only persistable slices from full state
 */
export function extractPersistableState(state: EnhancedRootState): DraftRestorableState {
  const persistableSlices: (keyof EnhancedRootState)[] = [
    'viewport',
    'numberLines',
    'secondaryLine', 
    'equivalentFractions',
    'labelVisibility',
    'customTicks',
    'jumpIndicators',
    'expressions',
    'constants',
    'preferences',
  ];

  const result: DraftRestorableState = {};
  
  for (const sliceName of persistableSlices) {
    if (state[sliceName]) {
      // @ts-ignore - we know these are valid keys
      result[sliceName] = state[sliceName];
    }
  }
  
  return result;
}

// =============================================================================
// UTILITY TYPES FOR ACTIONS
// =============================================================================

/**
 * Action types that should trigger auto-save
 */
export type AutoSaveActionType = 
  | `viewport/${string}`
  | `numberLines/${string}`
  | `secondaryLine/${string}`
  | `equivalentFractions/${string}`
  | `labelVisibility/${string}`
  | `customTicks/${string}`
  | `jumpIndicators/${string}`
  | `expressions/${string}`
  | `constants/${string}`;

/**
 * Action types that should NOT trigger auto-save
 */
export type NonAutoSaveActionType = 
  | `ui/${string}`
  | `share/${string}`
  | `draft/${string}`
  | `preferences/${string}`; // preferences handle their own persistence

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Validate that a draft state is compatible with current schema
 */
export function validateDraftState(draftState: any): {
  valid: boolean;
  errors: string[];
  warnings: string[];
} {
  const result = {
    valid: true,
    errors: [] as string[],
    warnings: [] as string[],
  };

  // Check for required slices
  const requiredSlices = ['viewport', 'numberLines', 'preferences'];
  for (const slice of requiredSlices) {
    if (!draftState[slice]) {
      result.errors.push(`Missing required slice: ${slice}`);
      result.valid = false;
    }
  }

  // Check viewport structure
  if (draftState.viewport) {
    const viewport = draftState.viewport;
    if (typeof viewport.scale !== 'number' || viewport.scale <= 0) {
      result.errors.push('Invalid viewport scale');
      result.valid = false;
    }
    if (typeof viewport.offsetX !== 'number') {
      result.errors.push('Invalid viewport offsetX');
      result.valid = false;
    }
  }

  // Warnings for missing optional slices
  const optionalSlices = ['expressions', 'jumpIndicators', 'annotations', 'equivalentFractions'];
  for (const slice of optionalSlices) {
    if (!draftState[slice]) {
      result.warnings.push(`Optional slice missing: ${slice}`);
    }
  }

  return result;
}

/**
 * Get size estimate for draft state
 */
export function estimateDraftSize(state: DraftRestorableState): number {
  try {
    return JSON.stringify(state).length;
  } catch (error) {
    return 0;
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

// Export the enhanced RootState as the default RootState
export type RootState = EnhancedRootState;

// Export compatibility types
export type { DraftState } from '../store/slices/draftSlice';